/**
 * 63.4's future, partial destruction step 3: what fell, drawn — each lying limb and heap in `GameState.debris` as the sim
 * places it (core/debris.ts debrisPieces), one instanced bark cylinder each. Presentation only: it reads the state and
 * rebuilds its instances when the debris array changes.
 *
 * Step 7 (the look, lane E): a piece a structure shed — the tower's radar head, the emplacement's shield (the prop log's
 * `part` entry names its debris) — is drawn as the part it was, in its team's colours, scaled to the sim's measured piece
 * and laid on its axis where the sim put it: the head face down, the shield flat. They are few (one a structure at
 * most), each its own small baked model.
 */
import * as THREE from 'three';
import { debrisFoot, debrisPieces, debrisRest } from '../core/debris';
import type { GroundDials } from '../core/fineTerrain';
import { treeFrame } from '../core/treeDraws';
import { TEAMS } from '../constants';
import type { DebrisState, GameState, PlanetState, StructureType } from '../types';
import { unitPaletteFor, type UnitPalette } from './unitPalette';
import { buildHeadPiece } from './towerModel';
import { buildShieldPiece } from './emplacementModel';
import { buildPdHeadPiece } from './pointDefenceLooks';   // 69.9
import type { PointDefenceVariant } from '../constants/action';
import { bakeStaticParts } from './meshBake';

/** What drew a structure's shed piece: its type's part model, and which way its face lies (+1 up, −1 down). */
const PART_PIECE: Partial<Record<StructureType, { build: (p: UnitPalette, variant?: PointDefenceVariant) => THREE.Group; face: 1 | -1 }>> = {
  tower: { build: buildHeadPiece, face: -1 },
  emplacement: { build: buildShieldPiece, face: 1 },
  pointDefence: { build: (p, variant) => buildPdHeadPiece(variant ?? 'cram', p), face: -1 },   // 69.9: its radar head (the dome, or the C-RAM's array)
};
const NEUTRAL: UnitPalette = unitPaletteFor({ primary: '#8a8f99' } as (typeof TEAMS)[number]);
/** The state a structure's shed pieces are read from (the prop log names them; the structure and its owner give type and team). */
export type DebrisContext = Partial<Pick<GameState, 'propEvents' | 'structures' | 'players'>>;

const BARK = '#5a4632';
const _up = new THREE.Vector3(0, 1, 0), _axis = new THREE.Vector3(), _q = new THREE.Quaternion(), _m = new THREE.Matrix4();
const _p = new THREE.Vector3(), _s = new THREE.Vector3();

export class DebrisView {
  private mesh: THREE.InstancedMesh | null = null;
  private readonly geo = new THREE.CylinderGeometry(1, 1, 1, 6);
  private readonly mat = new THREE.MeshLambertMaterial({ color: BARK, flatShading: true });
  private last: readonly DebrisState[] | undefined = undefined;
  /** 63.4 step 7: the shed parts drawn as themselves, by debris id. */
  private readonly parts = new Map<number, THREE.Group>();

  constructor(private readonly scene: THREE.Scene) {}

  /**
   * Follow the state's debris (undefined: none — the switch off); `ctx` names the pieces structures shed (step 7). 70.4:
   * each where the sim lays it — its foot on the fine ground (`fine`, the person scale's dials; null: the mesh) and resting
   * along it (core/debris.ts debrisFoot, debrisRest); it was drawn on the mesh, half a person under the fine ground.
   */
  update(planets: readonly PlanetState[], debris: readonly DebrisState[] | undefined, ctx: DebrisContext = {}, fine: GroundDials | null = null): void {
    if (debris === this.last) return;
    this.last = debris;
    const shed = this.shedParts(ctx);
    const all = debris ?? [];
    // 63.4 step 7: the pieces a structure shed are drawn as their parts; the rest as limbs
    const keep = new Set<number>();
    for (const d of all) {
      const from = shed.get(d.id);
      if (!from) continue;
      keep.add(d.id);
      if (!this.parts.has(d.id)) { const g = this.partModel(planets, d, from.type, from.team, fine, from.variant); if (g) { this.scene.add(g); this.parts.set(d.id, g); } }
    }
    for (const [id, g] of this.parts) if (!keep.has(id)) { this.scene.remove(g); g.traverse(o => (o as THREE.Mesh).geometry?.dispose()); this.parts.delete(id); }
    const list = all.filter(d => !keep.has(d.id));
    if (!this.mesh || this.mesh.instanceMatrix.count < list.length) {
      if (this.mesh) { this.scene.remove(this.mesh); this.mesh.dispose(); }
      this.mesh = new THREE.InstancedMesh(this.geo, this.mat, Math.max(16, list.length * 2));
      this.mesh.name = 'debris';
      this.mesh.frustumCulled = false;
      this.scene.add(this.mesh);
    }
    let n = 0;
    for (const d of list) {
      const planet = planets[d.planetIndex];
      if (!planet) continue;
      const { foot, up } = debrisFoot(planet, d, fine);
      for (const pc of debrisPieces(d, up, debrisRest(planet, d, fine))) {
        _axis.set(pc.b.x - pc.a.x, pc.b.y - pc.a.y, pc.b.z - pc.a.z);
        const l = _axis.length();
        if (!(l > 0)) continue;
        _q.setFromUnitVectors(_up, _axis.multiplyScalar(1 / l));
        _p.set(foot.x + (pc.a.x + pc.b.x) / 2, foot.y + (pc.a.y + pc.b.y) / 2, foot.z + (pc.a.z + pc.b.z) / 2);
        const rr = (pc.ra + pc.rb) / 2;
        _s.set(rr, l, rr);
        this.mesh.setMatrixAt(n++, _m.compose(_p, _q, _s));
      }
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** 63.4 step 7: which debris pieces a structure shed — from the prop log's part entries — with its type and team. */
  private shedParts(ctx: DebrisContext): Map<number, { type: StructureType; team: number | null; variant?: PointDefenceVariant }> {
    const out = new Map<number, { type: StructureType; team: number | null; variant?: PointDefenceVariant }>();
    for (const e of ctx.propEvents ?? []) {
      if (e.kind !== 'structure' || !e.debris?.length) continue;
      const s = ctx.structures?.find(x => x.id === e.id);
      if (!s || !PART_PIECE[s.type]) continue;
      const team = ctx.players?.find(p => p.id === s.ownerId)?.teamIndex ?? null;
      for (const id of e.debris) out.set(id, { type: s.type, team, ...(s.pd ? { variant: s.pd.variant } : {}) });
    }
    return out;
  }

  /** 63.4 step 7: a shed part's model laid where the sim put the piece (its axis from the piece's yaw, its foot on the ground under it). */
  private partModel(planets: readonly PlanetState[], d: DebrisState, type: StructureType, team: number | null, fine: GroundDials | null, variant?: PointDefenceVariant): THREE.Group | null {
    const planet = planets[d.planetIndex];
    if (!PART_PIECE[type] || !planet) return null;
    const { foot: f, up } = debrisFoot(planet, d, fine), ax = treeFrame(up, d.yaw)(0, 0, 1);   // (70.4: on the sim's seat)
    const foot = new THREE.Vector3(f.x, f.y, f.z);
    return layPartPiece(type, team === null ? NEUTRAL : unitPaletteFor(TEAMS[team % TEAMS.length]), d.length, foot, new THREE.Vector3(up.x, up.y, up.z), new THREE.Vector3(ax.x, ax.y, ax.z), variant);
  }

  dispose(): void {
    if (this.mesh) { this.scene.remove(this.mesh); this.mesh.dispose(); }
    this.geo.dispose(); this.mat.dispose();
    for (const g of this.parts.values()) { this.scene.remove(g); g.traverse(o => (o as THREE.Mesh).geometry?.dispose()); }
    this.parts.clear();
  }
}

/**
 * 63.4 step 7: a structure's shed part (the tower's radar head, the emplacement's shield) as a lying piece — its model in
 * `palette`, scaled so its long axis is `length`, laid along `axis` on the ground at `foot` (whose up is `up`): the head
 * face down, the shield flat; its lowest point on the ground. Null for a type that sheds nothing. The debris view and the
 * structure-forms sheet both lay them with it.
 */
export function layPartPiece(type: StructureType, palette: UnitPalette, length: number, foot: THREE.Vector3, up: THREE.Vector3, axis: THREE.Vector3, variant?: PointDefenceVariant): THREE.Group | null {
  const kind = PART_PIECE[type];
  if (!kind) return null;
  const model = kind.build(palette, variant);
  const box = new THREE.Box3().setFromObject(model), size = box.getSize(new THREE.Vector3()), centre = box.getCenter(new THREE.Vector3());
  model.position.set(-centre.x, -centre.y, 0);   // centred along and across; its face plane stays at z 0 … its depth
  const inner = new THREE.Group(); inner.add(model);
  bakeStaticParts(inner);
  const U = up.clone().normalize(), X = axis.clone().addScaledVector(U, -axis.dot(U)).normalize();
  const Z = U.clone().multiplyScalar(kind.face), Y = new THREE.Vector3().crossVectors(Z, X);
  const s = length / Math.max(1e-6, size.x);   // the sim's measured piece's length
  const lowest = Math.min(kind.face * box.min.z, kind.face * box.max.z) * s;   // its depth below the face plane, toward the ground
  const outer = new THREE.Group(); outer.add(inner);
  outer.matrixAutoUpdate = false;
  outer.matrix.makeBasis(X, Y, Z).scale(new THREE.Vector3(s, s, s)).setPosition(foot.clone().addScaledVector(U, -lowest));
  outer.userData.part = type === 'emplacement' ? 'shield' : 'head';
  return outer;
}

